import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Card,
    CardContent,
    IconButton,
    AppBar,
    Toolbar,
    Container,
    CircularProgress,
    Avatar,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Stack,
    Tooltip
} from '@mui/material';
import {
    ArrowBack,
    Delete,
    Visibility,
    VisibilityOff,
    Person,
    AccessTime,
    Store
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface Story {
    id: string;
    userId: string;
    username: string;
    userPhotoURL: string;
    imageUrl: string;
    thumbnailUrl: string;
    createdAt: any;
    expiresAt: any;
    viewCount: number;
    isActive: boolean;
    odaKodu: string;
    cafeName?: string;
}

const StoriesManagementPage: React.FC = () => {
    const navigate = useNavigate();

    const [stories, setStories] = useState<Story[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalStories, setTotalStories] = useState(0);

    // Dialog states
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
    const [selectedStory, setSelectedStory] = useState<Story | null>(null);

    const PRIMARY_COLOR = '#8B4513';
    const SECONDARY_COLOR = '#D2691E';

    const loadStories = useCallback(async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('adminToken');

            const response = await axios.get(`${API_BASE_URL}/api/stories`, {
                headers: { 'Authorization': `Bearer ${token}` },
                params: {
                    limit: rowsPerPage,
                    offset: page * rowsPerPage
                }
            });

            if (response.data.success) {
                setStories(response.data.data.stories || []);
                setTotalStories(response.data.data.total || 0);
            }

        } catch (error) {
            console.error('Error loading stories:', error);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage]);

    useEffect(() => {
        loadStories();
    }, [loadStories]);

    const handleDeleteStory = async () => {
        if (!selectedStory) return;

        try {
            const token = localStorage.getItem('adminToken');
            await axios.delete(`${API_BASE_URL}/api/stories/${selectedStory.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            setStories(stories.filter((story: Story) => story.id !== selectedStory.id));
            setDeleteDialogOpen(false);
            setSelectedStory(null);
        } catch (error) {
            console.error('Error deleting story:', error);
        }
    };

    const handleToggleStoryVisibility = async (story: Story) => {
        try {
            const token = localStorage.getItem('adminToken');
            await axios.patch(`${API_BASE_URL}/api/stories/${story.id}/visibility`, {
                isActive: !story.isActive
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            setStories(stories.map((s: Story) =>
                s.id === story.id ? { ...s, isActive: !s.isActive } : s
            ));

        } catch (error) {
            console.error('Error toggling story visibility:', error);
        }
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return 'Tarih yok';

        let date;
        if (timestamp.toDate && typeof timestamp.toDate === 'function') {
            date = timestamp.toDate();
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else if (typeof timestamp === 'string') {
            date = new Date(timestamp);
        } else {
            date = new Date(timestamp);
        }

        return date.toLocaleString('tr-TR');
    };

    const isExpired = (expiresAt: any) => {
        if (!expiresAt) return false;

        let date;
        if (expiresAt.toDate && typeof expiresAt.toDate === 'function') {
            date = expiresAt.toDate();
        } else if (expiresAt instanceof Date) {
            date = expiresAt;
        } else if (typeof expiresAt === 'string') {
            date = new Date(expiresAt);
        } else {
            date = new Date(expiresAt);
        }

        return new Date() > date;
    };

    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`
            }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress size={80} sx={{ color: 'white', mb: 2 }} />
                    <Typography variant="h6" color="white">
                        Yükleniyor...
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc' }}>
            {/* App Bar */}
            <AppBar
                position="static"
                sx={{
                    background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
                    boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
                }}
            >
                <Toolbar sx={{ minHeight: 70 }}>
                    <IconButton
                        color="inherit"
                        onClick={() => navigate('/admin/dashboard')}
                        sx={{ mr: 2 }}
                    >
                        <ArrowBack />
                    </IconButton>

                    <Typography variant="h5" component="div" sx={{
                        fontWeight: 700,
                        color: 'white',
                        flexGrow: 1
                    }}>
                        Hikaye Yönetimi
                    </Typography>
                </Toolbar>
            </AppBar>

            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                {/* Stats Cards */}
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 3,
                    mb: 4
                }}>
                    <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                        <Card sx={{
                            background: `linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${SECONDARY_COLOR} 100%)`,
                            color: 'white'
                        }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                    {totalStories}
                                </Typography>
                                <Typography variant="body1">
                                    Toplam Hikaye
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>

                    <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                        <Card sx={{
                            background: `linear-gradient(135deg, ${SECONDARY_COLOR} 0%, #CD853F 100%)`,
                            color: 'white'
                        }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                    {stories.filter(s => s.isActive && !isExpired(s.expiresAt)).length}
                                </Typography>
                                <Typography variant="body1">
                                    Aktif Hikaye
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                </Box>

                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Kullanıcı</TableCell>
                                    <TableCell>Görsel</TableCell>
                                    <TableCell>Mekan (Oda)</TableCell>
                                    <TableCell>Tarih</TableCell>
                                    <TableCell>Görüntülenme</TableCell>
                                    <TableCell>Durum</TableCell>
                                    <TableCell>İşlemler</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {stories.map((story: Story) => {
                                    const expired = isExpired(story.expiresAt);

                                    return (
                                        <TableRow key={story.id}>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar
                                                        src={story.userPhotoURL}
                                                        sx={{ width: 32, height: 32 }}
                                                    >
                                                        <Person />
                                                    </Avatar>
                                                    <Typography variant="body2">
                                                        {story.username}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="Büyütmek için tıkla">
                                                    <Box
                                                        sx={{
                                                            width: 50,
                                                            height: 80,
                                                            borderRadius: 1,
                                                            overflow: 'hidden',
                                                            cursor: 'pointer',
                                                            border: '1px solid #eee'
                                                        }}
                                                        onClick={() => {
                                                            setSelectedStory(story);
                                                            setImagePreviewOpen(true);
                                                        }}
                                                    >
                                                        <img
                                                            src={story.thumbnailUrl || story.imageUrl}
                                                            alt="Story"
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                        />
                                                    </Box>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} alignItems="center">
                                                    {story.cafeName ? (
                                                        <Chip icon={<Store fontSize="small" />} label={story.cafeName} size="small" color="primary" variant="outlined" />
                                                    ) : (
                                                        <Chip label={story.odaKodu} size="small" variant="outlined" />
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Stack spacing={0.5}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatDate(story.createdAt)}
                                                        </Typography>
                                                    </Box>
                                                    {expired && (
                                                        <Chip label="Süresi Doldu" size="small" color="default" sx={{ height: 20, fontSize: 10 }} />
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Visibility sx={{ fontSize: 16 }} />
                                                    <Typography variant="body2">
                                                        {story.viewCount}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={story.isActive ? 'Yayında' : 'Gizli'}
                                                    color={story.isActive ? 'success' : 'error'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Tooltip title={story.isActive ? "Gizle" : "Görünür Yap"}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleToggleStoryVisibility(story)}
                                                            color={story.isActive ? 'success' : 'warning'}
                                                        >
                                                            {story.isActive ? <Visibility /> : <VisibilityOff />}
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Sil">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setSelectedStory(story);
                                                                setDeleteDialogOpen(true);
                                                            }}
                                                            color="error"
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={totalStories}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e: any, newPage: any) => setPage(newPage)}
                        onRowsPerPageChange={(e: any) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </Paper>
            </Container>

            {/* Delete Story Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Hikayeyi Sil</DialogTitle>
                <DialogContent>
                    <Typography>
                        Bu hikayeyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
                    <Button onClick={handleDeleteStory} color="error" variant="contained">
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Preview Dialog */}
            <Dialog
                open={imagePreviewOpen}
                onClose={() => setImagePreviewOpen(false)}
                maxWidth="md"
            >
                <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', justifyContent: 'center', bgcolor: 'black' }}>
                    {selectedStory && (
                        <img
                            src={selectedStory.imageUrl}
                            alt="Story Full"
                            style={{ maxHeight: '80vh', maxWidth: '100%' }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default StoriesManagementPage;
